'use client';

import { Film, ImageIcon, type LucideIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslations } from 'next-intl';

import {
  COMPOSER_SKILL_PRESENTATION,
  DEFAULT_COMPOSER_SKILL_IDS,
  type VisibleComposerSkillId,
} from '@/components/visual-editor/chat/composerSkillPresentation';
import {
  CONVERSATION_CAPABILITY_CONTRACTS,
  CONVERSATION_CAPABILITY_PREVIEW_PRESENTATION,
  type ConversationCapabilityKey,
  type ConversationCapabilityMode,
  type ConversationTaskFamilyKey,
} from './contract';

type DeliveryCapabilityKey = Exclude<ConversationCapabilityKey, VisibleComposerSkillId>;

interface DeliveryCapabilityPresentation {
  titleKey: string;
  hintKey: string;
  previewImage: string;
  previewVideo?: string;
  icon: LucideIcon;
}

const COMPACT_TITLE_KEYS = {
  kagemane: 'deliveryComposerKagemane',
  buyerShow: 'deliveryComposerCompactTitleBuyerShow',
  productDetail: 'deliveryComposerCompactTitleProductDetail',
  poseVariation: 'deliveryComposerCompactTitlePoseVariation',
  lookbook: 'deliveryComposerCompactTitleLookbook',
  outfitMix: 'deliveryComposerCompactTitleOutfitMix',
  whiteBackground: 'deliveryComposerCompactTitleWhiteBackground',
  outfitStyleExtension: 'deliveryComposerCompactTitleOutfitStyleExtension',
  silhouetteExtension: 'deliveryComposerCompactTitleSilhouetteExtension',
  seamlessPattern: 'deliveryComposerCompactTitleSeamlessPattern',
  sketchToProduct: 'deliveryComposerCompactTitleSketchToProduct',
  productToSketch: 'deliveryComposerCompactTitleProductToSketch',
  productVideo: 'deliveryComposerCompactTitleProductVideo',
  modelVideo: 'deliveryComposerCompactTitleModelVideo',
  storyboardBreakdown: 'deliveryComposerCompactTitleStoryboardBreakdown',
  'ugc-bag-unboxing': 'deliveryComposerCompactTitleUgcBagUnboxing',
  'ugc-beauty-skincare': 'deliveryComposerCompactTitleUgcBeautySkincare',
  'ugc-try-on': 'deliveryComposerCompactTitleUgcTryOn',
} as const satisfies Record<ConversationCapabilityKey, string>;

const DELIVERY_CAPABILITY_PRESENTATION = {
  kagemane: { titleKey: 'deliveryComposerKagemane', hintKey: 'deliveryComposerKagemaneHint', previewImage: '/kagemane/bag-demo-poster.jpg', previewVideo: '/kagemane/bag-demo.mp4', icon: Film },
  buyerShow: { titleKey: 'deliveryComposerBuyerShow', hintKey: 'deliveryComposerBuyerShowHint', previewImage: '/landing/seo-cases/leather-coat-lookbook-angles/03-front-full-body-angle.webp', icon: ImageIcon },
  productDetail: { titleKey: 'deliveryComposerProductDetail', hintKey: 'deliveryComposerProductDetailHint', previewImage: CONVERSATION_CAPABILITY_PREVIEW_PRESENTATION.productDetail.coverMediaUrl, icon: ImageIcon },
  poseVariation: { titleKey: 'deliveryComposerPoseVariation', hintKey: 'deliveryComposerPoseVariationHint', previewImage: '/landing/seo-cases/leather-coat-lookbook-angles/04-side-profile-angle.webp', icon: ImageIcon },
  lookbook: { titleKey: 'deliveryComposerLookbook', hintKey: 'deliveryComposerLookbookHint', previewImage: CONVERSATION_CAPABILITY_PREVIEW_PRESENTATION.lookbook.coverMediaUrl, icon: ImageIcon },
  outfitMix: { titleKey: 'deliveryComposerOutfitMix', hintKey: 'deliveryComposerOutfitMixHint', previewImage: CONVERSATION_CAPABILITY_PREVIEW_PRESENTATION.outfitMix.coverMediaUrl, icon: ImageIcon },
  whiteBackground: { titleKey: 'deliveryComposerWhiteBackground', hintKey: 'deliveryComposerWhiteBackgroundHint', previewImage: CONVERSATION_CAPABILITY_PREVIEW_PRESENTATION.whiteBackground.coverMediaUrl, icon: ImageIcon },
  outfitStyleExtension: { titleKey: 'deliveryComposerOutfitStyleExtension', hintKey: 'deliveryComposerOutfitStyleExtensionHint', previewImage: CONVERSATION_CAPABILITY_PREVIEW_PRESENTATION.outfitStyleExtension.coverMediaUrl, icon: ImageIcon },
  silhouetteExtension: { titleKey: 'deliveryComposerSilhouetteExtension', hintKey: 'deliveryComposerSilhouetteExtensionHint', previewImage: CONVERSATION_CAPABILITY_PREVIEW_PRESENTATION.silhouetteExtension.coverMediaUrl, icon: ImageIcon },
  seamlessPattern: { titleKey: 'deliveryComposerSeamlessPattern', hintKey: 'deliveryComposerSeamlessPatternHint', previewImage: CONVERSATION_CAPABILITY_PREVIEW_PRESENTATION.seamlessPattern.coverMediaUrl, icon: ImageIcon },
  sketchToProduct: { titleKey: 'deliveryComposerSketchToProduct', hintKey: 'deliveryComposerSketchToProductHint', previewImage: CONVERSATION_CAPABILITY_PREVIEW_PRESENTATION.sketchToProduct.coverMediaUrl, icon: ImageIcon },
  productToSketch: { titleKey: 'deliveryComposerProductToSketch', hintKey: 'deliveryComposerProductToSketchHint', previewImage: CONVERSATION_CAPABILITY_PREVIEW_PRESENTATION.productToSketch.coverMediaUrl, icon: ImageIcon },
  productVideo: { titleKey: 'deliveryComposerProductVideo', hintKey: 'deliveryComposerProductVideoHint', previewImage: CONVERSATION_CAPABILITY_PREVIEW_PRESENTATION.productVideo.coverMediaUrl, previewVideo: CONVERSATION_CAPABILITY_PREVIEW_PRESENTATION.productVideo.effect.outputMediaUrl, icon: Film },
  modelVideo: { titleKey: 'deliveryComposerModelVideo', hintKey: 'deliveryComposerModelVideoHint', previewImage: CONVERSATION_CAPABILITY_PREVIEW_PRESENTATION.modelVideo.coverMediaUrl, previewVideo: CONVERSATION_CAPABILITY_PREVIEW_PRESENTATION.modelVideo.effect.outputMediaUrl, icon: Film },
  storyboardBreakdown: { titleKey: 'deliveryComposerStoryboardBreakdown', hintKey: 'deliveryComposerStoryboardBreakdownHint', previewImage: '/media-previews/v1/covers/storyboard-breakdown.jpg', icon: Film },
} as const satisfies Record<DeliveryCapabilityKey, DeliveryCapabilityPresentation>;

export interface ConversationCapabilityMenuItem {
  key: ConversationCapabilityKey;
  family: ConversationTaskFamilyKey;
  title: string;
  compactTitle: string;
  hint: string;
  compactHint: string;
  previewImage: string;
  previewVideo?: string;
  icon: LucideIcon;
  mode: ConversationCapabilityMode;
}

/**
 * The shared, translated presentation of the real capability contracts.
 * Consumers choose a family through CONVERSATION_CAPABILITY_MENU_BY_FAMILY;
 * they never maintain a second capability list.
 */
export function useConversationCapabilityMenu(
  visibleSkillIds: readonly VisibleComposerSkillId[] = DEFAULT_COMPOSER_SKILL_IDS,
): ReadonlyMap<ConversationCapabilityKey, ConversationCapabilityMenuItem> {
  const t = useTranslations('explore');
  const chatT = useTranslations('chat');

  return useMemo(() => {
    const visibleSkills = new Set([
      ...DEFAULT_COMPOSER_SKILL_IDS,
      ...visibleSkillIds,
    ]);
    const items = new Map<ConversationCapabilityKey, ConversationCapabilityMenuItem>();

    Object.values(CONVERSATION_CAPABILITY_CONTRACTS).forEach((contract) => {
      if (contract.execution.kind === 'skill') {
        const skillId = contract.key as VisibleComposerSkillId;
        if (!visibleSkills.has(skillId)) return;
        const presentation = COMPOSER_SKILL_PRESENTATION[skillId];
        items.set(contract.key, {
          key: contract.key,
          family: contract.family,
          title: chatT(presentation.labelKey as never),
          compactTitle: t(COMPACT_TITLE_KEYS[contract.key] as never),
          hint: chatT(presentation.descriptionKey as never),
          compactHint: t(contract.compactHintKey as never),
          previewImage: presentation.previewImage,
          previewVideo: presentation.previewVideo,
          icon: presentation.icon,
          mode: contract.mode,
        });
        return;
      }

      const presentation: DeliveryCapabilityPresentation =
        DELIVERY_CAPABILITY_PRESENTATION[contract.key as DeliveryCapabilityKey];
      items.set(contract.key, {
        key: contract.key,
        family: contract.family,
        title: t(presentation.titleKey as never),
        compactTitle: t(COMPACT_TITLE_KEYS[contract.key] as never),
        hint: t(presentation.hintKey as never),
        compactHint: t(contract.compactHintKey as never),
        previewImage: presentation.previewImage,
        previewVideo: presentation.previewVideo,
        icon: presentation.icon,
        mode: contract.mode,
      });
    });

    return items;
  }, [chatT, t, visibleSkillIds]);
}
